import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUsers, faHome, faBars, faTimes, faChartBar, faServer, faChartLine } from '@fortawesome/free-solid-svg-icons';
import Users from './Users';
import RevenueGraph from './RevenueGraph';
import HostingCost from './HostingCost';
import HostingReports from './HostingReports';

interface AdminPanelProps {
    activeTabExit: () => void;
}

type ActiveSection = 'reward-summary' | 'users' | 'hosting-cost' | 'reports' | 'settings';

function AdminPanel({ activeTabExit }: AdminPanelProps) {
    const [activeSection, setActiveSection] = useState<ActiveSection>('reward-summary');
    const [sidebarOpen, setSidebarOpen] = useState<boolean>(false);

    const sidebarItems = [
        {
            id: 'reward-summary' as ActiveSection,
            label: 'Reward Summary',
            icon: faChartLine,
            component: <RevenueGraph />
        },
        {
            id: 'users' as ActiveSection,
            label: 'Users',
            icon: faUsers,
            component: <Users />
        },
        {
            id: 'hosting-cost' as ActiveSection,
            label: 'Hosting Cost',
            icon: faServer,
            component: <HostingCost />
        },
        {
            id: 'reports' as ActiveSection,
            label: 'Reports',
            icon: faChartBar,
            component: <HostingReports />
        },
        {
            id: 'settings' as ActiveSection,
            label: 'System Settings',
            icon: faChartBar,
            component: (
                <div className="text-center py-12">
                    <FontAwesomeIcon icon={faChartBar} className="w-12 h-12 text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">System Settings Coming Soon</h3>
                    <p className="text-gray-500">System configuration options will be available here.</p>
                </div>
            )
        }
    ];

    const renderActiveComponent = () => {
        const activeItem = sidebarItems.find(item => item.id === activeSection);
        return activeItem?.component || <RevenueGraph />;
    };

    const handleSidebarItemClick = (sectionId: ActiveSection) => {
        setActiveSection(sectionId);
        setSidebarOpen(false); // Close mobile sidebar after selection
    };

    const getActiveItemLabel = () => {
        const activeItem = sidebarItems.find(item => item.id === activeSection);
        return activeItem?.label || 'Admin Panel';
    };

    return (
        <div className="flex min-h-screen bg-gray-50">
            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                ></div>
            )}

            {/* Sidebar */}
            <div className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out ${
                sidebarOpen ? 'translate-x-0' : '-translate-x-full'
            } lg:translate-x-0`}>
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-bold text-gray-800">Admin Panel</h2>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="lg:hidden p-2 rounded-md text-gray-500 hover:text-gray-700"
                    >
                        <FontAwesomeIcon icon={faTimes} className="w-5 h-5" />
                    </button>
                </div>
                <nav className="mt-6 flex-1">
                    {sidebarItems.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => handleSidebarItemClick(item.id)}
                            className={`w-full text-left px-6 py-3 flex items-center gap-3 transition-colors duration-200 ${
                                activeSection === item.id
                                    ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600'
                                    : 'text-gray-700 hover:bg-gray-50'
                            }`}
                        >
                            <FontAwesomeIcon icon={item.icon} className="w-5 h-5 flex-shrink-0" />
                            <span className="font-medium">{item.label}</span>
                        </button>
                    ))}
                </nav>
                <div className="p-6 border-t border-gray-200">
                    <button
                        type="button"
                        onClick={activeTabExit}
                        className="w-full gap-2 flex content-center items-center justify-center py-3 px-4 text-sm font-medium text-gray-900 focus:outline-none bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 transition-colors duration-200"
                    >
                        <FontAwesomeIcon icon={faHome} />
                        <span>Back to Home</span>
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex flex-col lg:ml-0">
                {/* Mobile header */}
                <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={() => setSidebarOpen(true)}
                            className="p-2 rounded-md text-gray-500 hover:text-gray-700"
                        >
                            <FontAwesomeIcon icon={faBars} className="w-6 h-6" />
                        </button>
                        <h1 className="text-lg font-semibold text-gray-900">{getActiveItemLabel()}</h1>
                        <button
                            onClick={activeTabExit}
                            className="p-2 rounded-md text-gray-500 hover:text-gray-700"
                        >
                            <FontAwesomeIcon icon={faHome} className="w-5 h-5" />
                        </button>
                    </div>
                </div>

                {/* Content area */}
                <div className="flex-1 overflow-auto">
                    <div className="p-4 lg:p-6">
                        {renderActiveComponent()}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminPanel;
